import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useFeatures } from "../lib/useFeatures";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { StackPlus, CheckCircle, MinusCircle, ArrowsSplit, Lock } from "@phosphor-icons/react";

const NONE = "__none__";

export default function BulkApplyWizard({ doc }) {
  const { can } = useFeatures();
  const [open, setOpen] = useState(false);
  const [regs, setRegs] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState({});       // registration_id -> bool
  const [globalRoute, setGlobalRoute] = useState(NONE);
  const [perEntity, setPerEntity] = useState(false);
  const [routeByReg, setRouteByReg] = useState({});    // registration_id -> template_id
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setResult(null);
    try {
      const [r, t] = await Promise.all([api.get("/registrations"), api.get("/approval-route-templates")]);
      setActiveId(r.data.active_registration_id);
      setRegs((r.data.registrations || []).filter((x) => x.id !== r.data.active_registration_id));
      setTemplates(t.data || []);
    } catch (e) { toast.error("Could not load registrations."); }
  };

  const openWizard = async () => { await load(); setSelected({}); setGlobalRoute(NONE); setPerEntity(false); setRouteByReg({}); setOpen(true); };
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allChecked = regs.length > 0 && selectedIds.length === regs.length;
  const toggleAll = () => setSelected(allChecked ? {} : Object.fromEntries(regs.map((r) => [r.id, true])));

  const apply = async () => {
    if (selectedIds.length === 0) return toast.error("Select at least one registration.");
    const payload = { registration_ids: selectedIds };
    if (perEntity) {
      const map = {};
      selectedIds.forEach((id) => { if (routeByReg[id] && routeByReg[id] !== NONE) map[id] = routeByReg[id]; });
      payload.per_entity_routes = map;
    } else if (globalRoute !== NONE) {
      payload.route_template_id = globalRoute;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/apply-to-registrations`, payload);
      setResult(data);
      toast.success(`Applied to ${data.created} registration(s)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const regName = (id) => regs.find((r) => r.id === id)?.name || id;

  if (!can("bulk_apply")) {
    return (
      <Button data-testid="apply-to-registrations-locked-btn" variant="outline" className="rounded-none border-dashed text-muted-foreground"
        onClick={() => toast.message("Bulk Apply is a Growth plan feature.", { description: "Upgrade in Admin → Billing to clone documents across your fleet." })}>
        <Lock size={16} className="mr-1" />Apply to Entities (Growth)
      </Button>
    );
  }

  return (
    <>
      <Button data-testid="apply-to-registrations-btn" onClick={openWizard} variant="outline" className="rounded-none border-black">
        <StackPlus size={16} className="mr-1" />Apply to Entities
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-xl max-h-[88vh] overflow-y-auto" data-testid="bulk-apply-dialog">
          <DialogHeader><DialogTitle>Apply “{doc.title}” to other registered entities</DialogTitle></DialogHeader>

          {!result ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                This creates an <strong>independent, disconnected copy</strong> in each selected entity, auto-tailored to that
                entity’s applicability (non-applicable requirements are dropped). Copies do not stay in sync with this master.
              </p>

              {regs.length === 0 ? (
                <div className="border border-border bg-secondary/40 p-4 text-sm text-muted-foreground" data-testid="bulk-no-targets">
                  No other registrations to apply to. Create more under Admin → Registrations.
                </div>
              ) : (
                <>
                  <div className="border border-border">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-secondary/40">
                      <span className="overline text-muted-foreground">Target entities</span>
                      <button data-testid="bulk-select-all" onClick={toggleAll} className="text-xs font-semibold underline">{allChecked ? "Clear all" : "Select all"}</button>
                    </div>
                    <div className="max-h-52 overflow-y-auto divide-y divide-border">
                      {regs.map((r) => (
                        <label key={r.id} data-testid={`bulk-target-${r.id}`} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-secondary/30">
                          <Checkbox checked={!!selected[r.id]} onCheckedChange={() => toggle(r.id)} className="rounded-none" data-testid={`bulk-check-${r.id}`} />
                          <span className="text-sm font-semibold">{r.name}</span>
                          {r.nerc_id && <span className="text-xs text-muted-foreground font-mono">{r.nerc_id}</span>}
                          {perEntity && selected[r.id] && (
                            <Select value={routeByReg[r.id] || NONE} onValueChange={(v) => setRouteByReg((m) => ({ ...m, [r.id]: v }))}>
                              <SelectTrigger data-testid={`bulk-route-${r.id}`} className="rounded-none h-7 ml-auto w-44 text-xs" onClick={(e) => e.preventDefault()}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE}>No approval route</SelectItem>
                                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={perEntity} onCheckedChange={(v) => setPerEntity(!!v)} className="rounded-none" data-testid="bulk-per-entity-toggle" />
                      <ArrowsSplit size={15} /> Choose a different approval route per entity
                    </label>
                    {!perEntity && (
                      <div className="flex items-center gap-2">
                        <span className="overline text-muted-foreground">Approval route</span>
                        <Select value={globalRoute} onValueChange={setGlobalRoute}>
                          <SelectTrigger data-testid="bulk-global-route" className="rounded-none h-8 flex-1 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>No approval route (leave as draft)</SelectItem>
                            {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({(t.steps || []).length} steps)</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-2" data-testid="bulk-apply-result">
              <p className="text-sm font-semibold">Created {result.created} of {result.total}.</p>
              <div className="border border-border divide-y divide-border max-h-72 overflow-y-auto">
                {result.results.map((r) => (
                  <div key={r.registration_id} data-testid={`bulk-result-${r.registration_id}`} className="flex items-center gap-2 px-3 py-2 text-sm">
                    {r.status === "created"
                      ? <CheckCircle size={16} weight="fill" className="text-green-600 shrink-0" />
                      : <MinusCircle size={16} weight="fill" className="text-muted-foreground shrink-0" />}
                    <span className="font-semibold">{r.name || regName(r.registration_id)}</span>
                    {r.status === "created" ? (
                      <span className="text-xs text-muted-foreground ml-auto">
                        {r.kept_requirements} reqs kept{r.dropped_requirements ? `, ${r.dropped_requirements} not applicable` : ""}{r.route_applied ? " · routed for approval" : ""}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground ml-auto">Skipped — {r.reason}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setOpen(false)} variant="outline" className="rounded-none">{result ? "Done" : "Cancel"}</Button>
            {!result && regs.length > 0 && (
              <Button data-testid="bulk-apply-submit" onClick={apply} disabled={busy || selectedIds.length === 0} className="rounded-none bg-primary hover:bg-primary/90">
                {busy ? "Applying…" : `Apply to ${selectedIds.length || ""} entit${selectedIds.length === 1 ? "y" : "ies"}`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
